import React, { useState, useMemo } from "react";
import {
  Typography,
  Paper,
  Stack,
  Box,
  Card, // Card is still imported but not used for individual comments
  CardContent, // CardContent is still imported but not used for individual comments
  Avatar,
  Chip,
  Rating,
  IconButton,
  Collapse,
  Button,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  ChatBubbleOutline,
  ExpandMore,
  Visibility,
  Person,
  Schedule,
  ThumbUp,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

const LatestDiveComments = ({ comments }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Updated container styles for a more defined, less diffused look and whiter background
  const containerStyles = useMemo(
    () => ({
      background: "#FFFFFF", // Explicitly white background
      // backdropFilter: "blur(20px)", // Removed for less diffused look
      borderRadius: "16px", // Slightly smaller border radius for a sharper edge
      boxShadow: theme.shadows[3], // Use a standard, less "floaty" shadow from the theme
      border: `1px solid ${theme.palette.grey[200]}`, // Lighter, more defined border
      transition: "all 0.3s ease-in-out", // Slightly faster transition
      width: "100%",
      flex: 1,
      mt: 2,
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-2px)", // Subtle lift on hover
        boxShadow: theme.shadows[6], // Slightly more pronounced shadow on hover
      },
    }),
    [theme] // Add theme as a dependency for useMemo
  );

  const headerStyles = useMemo(
    () => ({
      // Retain the diffused yellow tone as requested
      background:
        "linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 193, 7, 0.05) 100%)",
      borderBottom: `1px solid ${theme.palette.grey[200]}`, // Lighter, more defined bottom border
      p: 3,
    }),
    [theme]
  );

  // Helper function to get initials from name
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  // Determine how many comments to show
  const displayComments = showAll ? comments : comments?.slice(0, 3) || [];
  const hasMoreComments = comments && comments.length > 3;

  if (!comments || comments.length === 0) {
    return (
      <Paper sx={containerStyles}>
        {/* Header Section */}
        <Box sx={headerStyles}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                background: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
              }}
            >
              <ChatBubbleOutline sx={{ color: "white", fontSize: "20px" }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                background: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Dive Comments
            </Typography>
          </Stack>
        </Box>

        {/* Empty State */}
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              background: theme.palette.grey[50], // Lighter background for no content
              borderRadius: "12px", // Consistent border radius
              border: `1px dashed ${theme.palette.grey[300]}`, // Lighter dashed border for "no content"
            }}
          >
            <ChatBubbleOutline
              sx={{
                fontSize: 48,
                color: theme.palette.text.secondary, // Use theme's text secondary color
                mb: 2,
                opacity: 0.5,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.secondary, // Use theme's text secondary color
                mb: 1,
              }}
            >
              No Comments Yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
              }}
            >
              Be the first to share your diving experience!
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={containerStyles}>
      {/* Header Section */}
      <Box sx={headerStyles}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                background: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
              }}
            >
              <ChatBubbleOutline sx={{ color: "white", fontSize: "20px" }} />
            </Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                background: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Recent Dive Comments
            </Typography>
          </Stack>

          <Badge
            badgeContent={comments.length}
            sx={{
              "& .MuiBadge-badge": {
                background: "linear-gradient(135deg, #FF9800 0%, #FFC107 100%)",
                color: "white",
                fontWeight: 600,
              },
            }}
          >
            <Box />
          </Badge>
        </Stack>
      </Box>

      {/* Comments Section */}
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          {displayComments.map((comment, index) => (
            // Replaced Card with Box and applied card-like styles directly
            <Box
              key={comment.timestamp || index}
              sx={{
                background: theme.palette.grey[50], // Lighter background for comment sections
                border: `1px solid ${theme.palette.grey[200]}`, // Lighter border for comment sections
                borderRadius: "16px",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                p: 2.5, // Apply padding directly here
                "&:hover": {
                  background: theme.palette.grey[100], // Lighter hover background
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[2], // Lighter shadow on hover
                },
              }}
            >
              {/* Comment Header */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{ mb: 2 }}
              >
                <Avatar
                  sx={{
                    background:
                      "linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)",
                    width: 40,
                    height: 40,
                    fontSize: "14px",
                    fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                  }}
                >
                  {getInitials(comment.diverName)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ color: theme.palette.primary.main }} // Use theme's primary color for diver name
                    >
                      {comment.diverName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                    >
                      •
                    </Typography>
                    <Typography
                      variant="caption"
                      color={theme.palette.text.secondary}
                    >
                      {formatDate(comment.timestamp)}
                    </Typography>
                  </Stack>

                  {/* Visibility Rating */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="Visibility Rating - 1 to 5 scale, 10' / 3m per point">
                      <Chip
                        icon={
                          <Visibility sx={{ fontSize: "14px !important" }} />
                        }
                        label={`${comment.visibilityRating}/5`}
                        size="small"
                        sx={{
                          // No visual change from clickable={false} needed here if default non-clickable look is desired
                          // cursor: 'default', // Optional: explicitly set cursor if it's not already default
                          height: 24,
                          backgroundColor: theme.palette.success.light + "30", // Lighter background for chip
                          border: `1px solid ${theme.palette.success.main}50`, // Lighter border
                          color: theme.palette.success.dark, // Keep dark for contrast
                          fontSize: "11px",
                          fontWeight: 600,
                          "& .MuiChip-icon": {
                            fontSize: "14px",
                          },
                        }}
                        onClick={() => {}} // Provide a no-op function to prevent the error
                        clickable={false}   // Ensure it doesn't adopt button-like styles
                      />
                    </Tooltip>
                  </Stack>
                </Box>
              </Stack>

              {/* Comment Text */}
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary, // Use theme's text primary color
                  lineHeight: 1.6,
                  fontSize: "14px",
                  pl: 6, // Align with avatar
                }}
              >
                {comment.comment}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Show More/Less Button */}
        {hasMoreComments && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              onClick={() => setShowAll(!showAll)}
              sx={{
                background:
                  "linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)",
                border: "1px solid rgba(255, 152, 0, 0.3)",
                color: "#FF9800",
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: 600,
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 193, 7, 0.15) 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.2)",
                },
              }}
            >
              {showAll
                ? "Show Less"
                : `Show ${comments.length - 3} More Comments`}
            </Button>
          </Box>
        )}

        {/* Stats Footer */}
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          {" "}
          {/* Lighter border for footer */}
          <Typography
            variant="caption"
            color={theme.palette.grey[700]} // Lighter grey for footer text
            sx={{
              textAlign: "center",
              display: "block",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {comments.length} {comments.length === 1 ? "comment" : "comments"}{" "}
            from fellow divers
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default LatestDiveComments;
